import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Route, MapPin, Plus, Trash2, Loader2, History, Navigation, Clock, ArrowDown, Home, Bot, Download, Upload, FileSpreadsheet, CheckCircle, XCircle } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";

const RoutePlanning = ({ user, onLogout, Layout }) => {
  const [mode, setMode] = useState("one_pickup_multi_delivery");
  const [pickupPoints, setPickupPoints] = useState([{ label: "", address: "" }]);
  const [deliveryPoints, setDeliveryPoints] = useState([{ label: "", address: "" }]);
  const [startLocation, setStartLocation] = useState({ label: "Start", address: "" });
  const [endAtStart, setEndAtStart] = useState("true");
  const [endLocation, setEndLocation] = useState({ label: "End", address: "" });
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [activeHistoryId, setActiveHistoryId] = useState(null);
  const [considerTraffic, setConsiderTraffic] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [mapsKey, setMapsKey] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkJobs, setBulkJobs] = useState([]);
  const [bulkTraffic, setBulkTraffic] = useState(false);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);

  useEffect(() => {
    fetchHistory();
    fetchMapsKey();
    fetchBulkJobs();
  }, []);

  const fetchMapsKey = async () => {
    try {
      const res = await axios.get(`${API}/route-planning/maps-key`);
      setMapsKey(res.data.key);
    } catch (e) { console.error("Maps key fetch failed", e); }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/route-planning/history?limit=15`);
      setHistory(res.data);
    } catch (e) { console.error("History fetch failed", e); }
    finally { setLoadingHistory(false); }
  };

  // Load Google Maps script
  useEffect(() => {
    if (!mapsKey || document.getElementById("google-maps-script")) return;
    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${mapsKey}&libraries=places,geometry`;
    script.async = true;
    document.body.appendChild(script);
  }, [mapsKey]);

  // Render map when result is available
  const renderMap = useCallback(() => {
    if (!result || !window.google || !mapRef.current) return;

    const google = window.google;
    const map = new google.maps.Map(mapRef.current, {
      zoom: 10,
      center: { lat: 20.5937, lng: 78.9629 },
      mapTypeControl: false,
      streetViewControl: false,
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_TOP
      }
    });
    mapInstance.current = map;

    const bounds = new google.maps.LatLngBounds();
    const geocoder = new google.maps.Geocoder();

    // Draw actual road path from encoded polyline
    if (result.polyline && google.maps.geometry) {
      try {
        const path = google.maps.geometry.encoding.decodePath(result.polyline);
        if (path.length > 0) {
          new google.maps.Polyline({
            path,
            geodesic: true,
            strokeColor: "#00A699",
            strokeOpacity: 0.85,
            strokeWeight: 5,
            map,
          });
          path.forEach(p => bounds.extend(p));
        }
      } catch (e) {
        console.error("Polyline decode failed:", e);
      }
    }

    // Place markers for all points
    const allPoints = [
      { address: result.start_location?.address, label: "S", title: result.start_location?.label || "Start", color: "#3b82f6" },
      ...(result.optimized_stops || []).map((s, i) => ({
        address: s.address, label: `${i + 1}`, title: `${s.label} (${s.type})`,
        color: s.type === "pickup" ? "#22c55e" : "#ef4444"
      })),
      { address: result.end_location?.address, label: "E", title: result.end_location?.label || "End", color: "#3b82f6" }
    ];

    allPoints.forEach((point) => {
      if (!point.address) return;
      geocoder.geocode({ address: point.address }, (results, status) => {
        if (status === "OK" && results[0]) {
          const pos = results[0].geometry.location;
          bounds.extend(pos);
          new google.maps.Marker({
            position: pos,
            map,
            label: { text: point.label, color: "white", fontWeight: "bold", fontSize: "11px" },
            title: point.title,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              fillColor: point.color,
              fillOpacity: 1,
              strokeWeight: 2,
              strokeColor: "white",
              scale: 15,
            },
          });
          map.fitBounds(bounds);
        }
      });
    });
  }, [result]);

  useEffect(() => {
    if (result && window.google) {
      setTimeout(renderMap, 300);
    }
  }, [result, renderMap]);

  const addPoint = (type) => {
    if (type === "pickup") setPickupPoints((prev) => [...prev, { label: "", address: "" }]);
    else setDeliveryPoints((prev) => [...prev, { label: "", address: "" }]);
  };

  const removePoint = (type, index) => {
    if (type === "pickup") {
      if (pickupPoints.length <= 1) return;
      setPickupPoints((prev) => prev.filter((_, i) => i !== index));
    } else {
      if (deliveryPoints.length <= 1) return;
      setDeliveryPoints((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const updatePoint = (type, index, field, value) => {
    const setter = type === "pickup" ? setPickupPoints : setDeliveryPoints;
    setter((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  };

  const handleExportRoute = () => {
    if (!result) return;
    const rows = [["Sequence", "Type", "Label", "Address", "Distance from Previous", "Duration from Previous"]];
    rows.push(["Start", "-", result.start_location?.label || "Start", result.start_location?.address || "", "-", "-"]);
    (result.optimized_stops || []).forEach((s) => {
      rows.push([s.sequence, s.type, s.label, s.address, s.distance_from_prev, s.duration_from_prev]);
    });
    if (result.return_leg) {
      rows.push(["End", "return", result.end_location?.label || "End", result.end_location?.address || "", result.return_leg.distance, result.return_leg.duration]);
    } else {
      rows.push(["End", "-", result.end_location?.label || "End", result.end_location?.address || "", "-", "-"]);
    }
    rows.push([]);
    rows.push(["Total Distance", result.total_distance]);
    rows.push(["Total Duration", result.total_duration]);
    rows.push(["Total Stops", result.total_stops]);
    if (result.ai_summary) {
      rows.push([]);
      rows.push(["AI Summary", result.ai_summary.replace(/\n/g, " ")]);
    }
    const csv = rows.map(r => r.map(v => `"${v}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `route_plan_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fetchBulkJobs = async () => {
    try {
      const res = await axios.get(`${API}/route-planning/bulk/jobs?limit=10`);
      setBulkJobs(res.data);
    } catch (e) { console.error("Bulk jobs fetch failed", e); }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("consider_traffic", bulkTraffic);
      const res = await axios.post(`${API}/route-planning/bulk/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setBulkResult(res.data);
      fetchBulkJobs();
      fetchHistory();
      toast.success(`Bulk route planning complete: ${res.data.completed} succeeded, ${res.data.failed} failed`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Bulk upload failed");
    } finally {
      setBulkUploading(false);
      e.target.value = "";
    }
  };

  const handleExportBulkJob = async (jobId) => {
    try {
      const res = await axios.get(`${API}/route-planning/bulk/jobs/${jobId}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bulk_route_results_${jobId.slice(0, 8)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  const handleOptimize = async () => {
    const validPickups = pickupPoints.filter((p) => p.address.trim());
    const validDeliveries = deliveryPoints.filter((p) => p.address.trim());
    const hasStart = startLocation.address.trim();

    // Need at least 2 points total to calculate a route
    const totalPoints = (hasStart ? 1 : 0) + validPickups.length + validDeliveries.length;
    if (totalPoints < 2) {
      toast.error("Enter at least 2 locations to calculate a route");
      return;
    }

    setCalculating(true);
    setResult(null);
    try {
      const body = {
        mode,
        pickup_points: validPickups,
        delivery_points: validDeliveries,
        start_location: hasStart ? startLocation : null,
        end_at_start: endAtStart === "true",
        end_location: endAtStart === "false" ? endLocation : null,
        consider_traffic: considerTraffic,
      };
      const res = await axios.post(`${API}/route-planning/optimize`, body);
      setResult(res.data);
      setActiveHistoryId(res.data.history_id);
      fetchHistory();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Route optimization failed");
    } finally {
      setCalculating(false);
    }
  };

  const loadHistoryDetail = async (historyId) => {
    try {
      const res = await axios.get(`${API}/route-planning/history/${historyId}`);
      if (res.data && res.data.success) {
        setResult(res.data);
        setActiveHistoryId(historyId);
      }
    } catch (e) {
      toast.error("Failed to load route details");
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-2">Route Planning</h1>
          <p className="text-slate-600">Optimize delivery routes for riders</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Result - shown above form when available */}
            {result && (
              <>
                <Card className="border-0 shadow-lg overflow-hidden">
                  <div ref={mapRef} style={{ height: 400, width: "100%" }} className="bg-slate-100" />
                </Card>
                <div className="grid grid-cols-3 gap-4">
                  <Card className="border-0 shadow-lg border-l-4 border-l-teal-500"><CardContent className="pt-4 text-center"><p className="text-2xl font-bold text-teal-700">{result.total_distance}</p><p className="text-xs text-slate-500">Total Distance</p></CardContent></Card>
                  <Card className="border-0 shadow-lg border-l-4 border-l-blue-500"><CardContent className="pt-4 text-center"><p className="text-2xl font-bold text-blue-700">{result.total_duration}</p><p className="text-xs text-slate-500">Total Time</p></CardContent></Card>
                  <Card className="border-0 shadow-lg border-l-4 border-l-orange-500"><CardContent className="pt-4 text-center"><p className="text-2xl font-bold text-orange-700">{result.total_stops}</p><p className="text-xs text-slate-500">Stops</p></CardContent></Card>
                </div>
                {result.ai_summary && (
                  <Card className="border-0 shadow-lg bg-gradient-to-r from-teal-50 to-emerald-50">
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-2">
                        <Bot className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-semibold text-teal-700 mb-1">Route Summary</p>
                          <p className="text-sm text-slate-700 leading-relaxed">{result.ai_summary}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
                <Card className="border-0 shadow-lg">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm">Optimized Route Sequence</CardTitle>
                      <button onClick={handleExportRoute} className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-700"><Download className="w-3.5 h-3.5" /> Export</button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3 py-2">
                      <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold">S</div>
                      <div className="flex-1"><p className="text-sm font-medium text-slate-800">{result.start_location?.label}</p><p className="text-xs text-slate-500">{result.start_location?.address}</p></div>
                    </div>
                    {result.optimized_stops?.map((stop, i) => (
                      <div key={i}>
                        <div className="flex items-center gap-3 pl-4 py-1"><ArrowDown className="w-3 h-3 text-slate-300" /><span className="text-xs text-slate-400">{stop.distance_from_prev} · {stop.duration_from_prev}</span></div>
                        <div className="flex items-center gap-3 py-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${stop.type === "pickup" ? "bg-green-500" : "bg-red-500"}`}>{stop.sequence}</div>
                          <div className="flex-1"><div className="flex items-center gap-2"><p className="text-sm font-medium text-slate-800">{stop.label}</p><Badge className={`text-xs border-0 ${stop.type === "pickup" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{stop.type}</Badge></div><p className="text-xs text-slate-500">{stop.address}</p></div>
                        </div>
                      </div>
                    ))}
                    {result.return_leg && (<div className="flex items-center gap-3 pl-4 py-1"><ArrowDown className="w-3 h-3 text-slate-300" /><span className="text-xs text-slate-400">{result.return_leg.distance} · {result.return_leg.duration}</span></div>)}
                    <div className="flex items-center gap-3 py-2">
                      <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold">E</div>
                      <div className="flex-1"><p className="text-sm font-medium text-slate-800">{result.end_location?.label}</p><p className="text-xs text-slate-500">{result.end_location?.address}</p></div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Route className="w-5 h-5" style={{ color: "#00A699" }} />
                  Plan Route
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Mode */}
                <div>
                  <Label>Route Type</Label>
                  <Select value={mode} onValueChange={(v) => { setMode(v); if (v === "one_pickup_multi_delivery") { setPickupPoints([pickupPoints[0] || { label: "", address: "" }]); } else { setDeliveryPoints([deliveryPoints[0] || { label: "", address: "" }]); } }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="one_pickup_multi_delivery">One Pickup → Multiple Deliveries</SelectItem>
                      <SelectItem value="multi_pickup_one_delivery">Multiple Pickups → One Delivery</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Start Location */}
                <div className="bg-blue-50 p-4 rounded-lg space-y-2">
                  <Label className="text-blue-700 flex items-center gap-1"><Home className="w-3.5 h-3.5" /> Rider Start Location</Label>
                  <div className="flex gap-2">
                    <Input placeholder="Label" value={startLocation.label} onChange={(e) => setStartLocation((p) => ({ ...p, label: e.target.value }))} className="w-32" />
                    <Input placeholder="Address, pincode, coordinates, or locality" value={startLocation.address} onChange={(e) => setStartLocation((p) => ({ ...p, address: e.target.value }))} className="flex-1" />
                  </div>
                </div>

                {/* Pickup Points */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-green-700 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Pickup Points</Label>
                    {(mode === "multi_pickup_one_delivery" || pickupPoints.length === 0) && (
                      <button onClick={() => addPoint("pickup")} className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700"><Plus className="w-3.5 h-3.5" /> Add Pickup</button>
                    )}
                  </div>
                  {pickupPoints.map((p, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-white bg-green-500">{i + 1}</div>
                      <Input placeholder="Label (optional)" value={p.label} onChange={(e) => updatePoint("pickup", i, "label", e.target.value)} className="w-32" />
                      <Input placeholder="Address, pincode, or locality" value={p.address} onChange={(e) => updatePoint("pickup", i, "address", e.target.value)} className="flex-1" />
                      {pickupPoints.length > 1 && (
                        <button onClick={() => removePoint("pickup", i)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </div>
                  ))}
                  {mode === "one_pickup_multi_delivery" && pickupPoints.length === 1 && <p className="text-xs text-slate-400">Single pickup mode — one pickup location</p>}
                </div>

                {/* Delivery Points */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-red-600 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Delivery Points</Label>
                    {(mode === "one_pickup_multi_delivery" || deliveryPoints.length === 0) && (
                      <button onClick={() => addPoint("delivery")} className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600"><Plus className="w-3.5 h-3.5" /> Add Delivery</button>
                    )}
                  </div>
                  {deliveryPoints.map((p, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-white bg-red-500">{i + 1}</div>
                      <Input placeholder="Label (optional)" value={p.label} onChange={(e) => updatePoint("delivery", i, "label", e.target.value)} className="w-32" />
                      <Input placeholder="Address, pincode, or locality" value={p.address} onChange={(e) => updatePoint("delivery", i, "address", e.target.value)} className="flex-1" />
                      {deliveryPoints.length > 1 && (
                        <button onClick={() => removePoint("delivery", i)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </div>
                  ))}
                  {mode === "multi_pickup_one_delivery" && deliveryPoints.length === 1 && <p className="text-xs text-slate-400">Single delivery mode — one delivery location</p>}
                </div>

                {/* End Location */}
                <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                  <Label className="text-slate-600">Rider End Location</Label>
                  <Select value={endAtStart} onValueChange={setEndAtStart}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Return to Start Location</SelectItem>
                      <SelectItem value="false">Different End Location</SelectItem>
                    </SelectContent>
                  </Select>
                  {endAtStart === "false" && (
                    <div className="flex gap-2">
                      <Input placeholder="Label" value={endLocation.label} onChange={(e) => setEndLocation((p) => ({ ...p, label: e.target.value }))} className="w-32" />
                      <Input placeholder="End address" value={endLocation.address} onChange={(e) => setEndLocation((p) => ({ ...p, address: e.target.value }))} className="flex-1" />
                    </div>
                  )}
                </div>

                {/* Traffic toggle */}
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={considerTraffic}
                      onChange={(e) => setConsiderTraffic(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-[#00A699] focus:ring-[#00A699]"
                    />
                    <span className="text-sm text-slate-700">Consider real-time traffic</span>
                  </label>
                  {considerTraffic && <span className="text-xs text-teal-600 bg-teal-50 px-2 py-0.5 rounded">Live traffic enabled</span>}
                </div>

                <Button onClick={handleOptimize} disabled={calculating} className="w-full" style={{ backgroundColor: "#00A699" }}>
                  {calculating ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Optimizing Route...</>) : (<><Navigation className="w-4 h-4 mr-2" />Optimize Route</>)}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* History */}
          <div className="lg:col-span-1">
            <Card className="border-0 shadow-lg">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2"><History className="w-4 h-4 text-slate-500" /> Route History</CardTitle>
              </CardHeader>
              <CardContent>
                {loadingHistory ? (
                  <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
                ) : history.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-6">No routes planned yet</p>
                ) : (
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {history.map((item, i) => (
                      <button
                        key={item.id || i}
                        onClick={() => loadHistoryDetail(item.id)}
                        className={`w-full text-left p-3 rounded-lg border transition-colors ${
                          activeHistoryId === item.id
                            ? "border-teal-400 bg-teal-50"
                            : "border-slate-100 hover:bg-teal-50 hover:border-teal-200"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-bold text-teal-600">#{item.search_id || "-"}</span>
                            {item.source === "bulk" && (
                              <Badge className="bg-purple-100 text-purple-600 border-0 text-[10px] px-1 py-0">
                                Bulk #{item.bulk_job_number || "-"}
                              </Badge>
                            )}
                          </div>
                          <div className="flex gap-2 text-xs text-slate-500">
                            <span>{item.total_stops} stops</span>
                            <span>{item.total_distance}</span>
                          </div>
                        </div>
                        <p className="font-medium text-sm text-slate-700 truncate">{item.start_address}</p>
                        {item.bulk_route_id && <p className="text-xs text-purple-500 truncate">{item.bulk_route_id}</p>}
                        <div className="flex items-center justify-between mt-1 text-xs text-slate-400">
                          <span>{item.user_name}</span>
                          <span>{formatToIST(item.created_at)}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Bulk Route Planning */}
        <hr className="border-slate-200" />
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" style={{ color: "#00A699" }} />
              Bulk Route Planning
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-700">Need a template?</p>
                <p className="text-xs text-slate-500">Download the CSV template with sample data</p>
              </div>
              <Button variant="outline" onClick={async () => {
                try {
                  const res = await axios.get(`${API}/route-planning/bulk/template`, { responseType: "blob" });
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "route_planning_template.csv";
                  a.click();
                  URL.revokeObjectURL(url);
                } catch (e) { toast.error("Failed to download template"); }
              }} className="text-sm">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Download Template
              </Button>
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={bulkTraffic} onChange={(e) => setBulkTraffic(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-[#00A699] focus:ring-[#00A699]" />
                <span className="text-sm text-slate-700">Consider real-time traffic</span>
              </label>
              {bulkTraffic && <span className="text-xs text-teal-600 bg-teal-50 px-2 py-0.5 rounded">Live traffic enabled</span>}
            </div>

            <div className="flex items-center gap-4">
              <label className="flex-1">
                <div className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${bulkUploading ? "border-slate-300 bg-slate-50" : "border-slate-200 hover:border-teal-400 hover:bg-teal-50"}`}>
                  {bulkUploading ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin text-teal-600" />
                      <span className="text-sm text-slate-600">Processing routes...</span>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                      <p className="text-sm text-slate-600">Click to upload CSV file</p>
                      <p className="text-xs text-slate-400 mt-1">CSV with columns: route_id, point_type (start/pickup/delivery/end), label, address (address, pincode, or coordinates)</p>
                    </>
                  )}
                  <input type="file" accept=".csv" onChange={handleBulkUpload} disabled={bulkUploading} className="hidden" />
                </div>
              </label>
            </div>

            {/* Bulk Result */}
            {bulkResult && (
              <div className="space-y-3">
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-xs font-bold text-purple-600">Bulk Job #{bulkResult.bulk_job_number || "-"}</span>
                  <span className="text-slate-600">Routes: <strong>{bulkResult.total_routes}</strong></span>
                  <span className="text-green-600">Completed: <strong>{bulkResult.completed}</strong></span>
                  {bulkResult.failed > 0 && <span className="text-red-600">Failed: <strong>{bulkResult.failed}</strong></span>}
                </div>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {Object.entries(bulkResult.results).map(([rid, res]) => (
                    <div key={rid} className={`p-3 rounded-lg border text-sm ${res.success ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          {res.success ? <CheckCircle className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-red-500" />}
                          <span className="font-medium text-slate-800">{rid}</span>
                        </div>
                        {res.success && (
                          <div className="flex gap-3 text-xs text-slate-500">
                            <span>{res.total_stops} stops</span>
                            <span>{res.total_distance}</span>
                            <span>{res.total_duration}</span>
                          </div>
                        )}
                      </div>
                      {res.success ? (
                        <div className="ml-6 text-xs text-slate-600">
                          <span>{res.start}</span>
                          {res.stops?.map((s, i) => (
                            <span key={i}> → {s.label} ({s.distance_from_prev})</span>
                          ))}
                        </div>
                      ) : (
                        <p className="ml-6 text-xs text-red-600">{res.error}</p>
                      )}
                    </div>
                  ))}
                </div>
                <Button variant="outline" onClick={() => handleExportBulkJob(bulkResult.job_id)} className="text-sm">
                  <Download className="w-4 h-4 mr-2" /> Export Results CSV
                </Button>
              </div>
            )}

            {/* Bulk Job History */}
            {bulkJobs.length > 0 && !bulkResult && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Previous Bulk Jobs</p>
                <div className="space-y-2">
                  {bulkJobs.map((job) => (
                    <div key={job.id} className="p-3 rounded-lg border border-slate-100 text-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-purple-600">Bulk Job #{job.bulk_job_number || "-"}</span>
                          </div>
                          <p className="font-medium text-slate-700">{job.file_name}</p>
                          <p className="text-xs text-slate-400">{job.total_routes} routes · {job.completed} completed · {formatToIST(job.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-2">
                        {job.file_s3_path && (
                          <Button variant="outline" size="sm" onClick={async () => {
                            try {
                              const res = await axios.get(`${API}/route-planning/bulk/jobs/${job.id}/download-file`);
                              window.open(res.data.url, '_blank');
                            } catch (e) { toast.error("File not available"); }
                          }}>
                            <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Source File
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => handleExportBulkJob(job.id)}>
                          <Download className="w-3.5 h-3.5 mr-1" /> Results
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default RoutePlanning;
